import { Component, OnInit } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { MatDialog } from '@angular/material/dialog';
import { DataLibraryMigrateDialogComponent } from 'app/dialogs';

import * as models from 'app/models';
import * as services from 'app/services';
import * as viewmodels from 'app/viewmodels';

@Component({
    selector: 'app-data-library-setup',
    templateUrl: './data-library-setup.component.html',
    styleUrls: ['./data-library-setup.component.scss'],
    standalone: false
})
export class DataLibrarySetupComponent implements OnInit {

  dataCollectionDefinitions: Array<models.DataCollectionDefinition> = [];
  initialDataCollectionDefinitions: Array<models.DataCollectionDefinition> = [];

  unitList: viewmodels.DataCollectionDefinitionUnit[] = [];

  saving: boolean = false;
  searchText: string = '';
  newDefinitionName: string = '';
  newDefinitionUnit: string = '';
  editingDefinitionId: string = null;
  editingDefinitionName: string = '';
  editingDefinitionUnit: string = '';

  get dirty(): boolean {
    let x = JSON.stringify(this.initialDataCollectionDefinitions);
    let y = JSON.stringify(this.dataCollectionDefinitions);
    return x !== y;
  }

  get filteredDataCollectionDefinitions(): Array<models.DataCollectionDefinition> {
    let search = this.searchText.trim().toLocaleLowerCase();

    return this.dataCollectionDefinitions.filter(definition => {
      let unit = this.getUnitLabel(definition.unit).toLocaleLowerCase();
      return !search || definition.name?.toLocaleLowerCase().includes(search) || unit.includes(search);
    });
  }

  constructor(
    private dataLibraryService: services.DataLibraryService
    , private helperService: services.HelperService
    , private firestore: AngularFirestore
    , private notifierService: services.NotifierService
    , private dialog: MatDialog
  ) { 
    this.unitList = this.helperService.sortArrayByStringField(new viewmodels.DataCollectionDefinitionUnits().list, 'display');
  }

  ngOnInit(): void {
    this.getDataLibrary();
  }

  async getDataLibrary() {
    //set grid to show that it is loading
    //this.gridOptions.api.showLoadingOverlay();

    //find the company and go get the users for that company
    let sensors = await this.dataLibraryService.getAll();
    this.dataCollectionDefinitions = this.helperService.sortArrayByStringField(sensors, 'name');
    this.initialDataCollectionDefinitions = this.helperService.deepCopy(this.dataCollectionDefinitions);
    this.cancelEdit();
  }

  add() {
    let name = this.newDefinitionName.trim();
    if (!name || !this.newDefinitionUnit) return;

    if (this.hasDuplicateName(name)) {
      this.notifierService.error('A Data Library item with this name already exists');
      return;
    }

    this.dataCollectionDefinitions.push({
      id: this.firestore.createId(),
      name: name,
      unit: this.newDefinitionUnit
    });
    this.dataCollectionDefinitions = this.helperService.sortArrayByStringField(this.dataCollectionDefinitions, 'name');
    this.newDefinitionName = '';
    this.newDefinitionUnit = '';
  }

  remove(n: number){
    this.dataCollectionDefinitions.splice(n, 1);
  }

  removeDefinition(definition: models.DataCollectionDefinition) {
    let index = this.dataCollectionDefinitions.indexOf(definition);
    if (index >= 0) this.remove(index);
  }

  edit(definition: models.DataCollectionDefinition) {
    this.editingDefinitionId = definition.id;
    this.editingDefinitionName = definition.name;
    this.editingDefinitionUnit = definition.unit;
  }

  applyEdit(definition: models.DataCollectionDefinition) {
    let name = this.editingDefinitionName.trim();
    if (!name || !this.editingDefinitionUnit) return;

    if (this.hasDuplicateName(name, definition.id)) {
      this.notifierService.error('A Data Library item with this name already exists');
      return;
    }

    definition.name = name;
    definition.unit = this.editingDefinitionUnit;
    this.dataCollectionDefinitions = this.helperService.sortArrayByStringField(this.dataCollectionDefinitions, 'name');
    this.cancelEdit();
  }

  cancelEdit() {
    this.editingDefinitionId = null;
    this.editingDefinitionName = '';
    this.editingDefinitionUnit = '';
  }

  getUnit(unitId: string): viewmodels.DataCollectionDefinitionUnit {
    return this.unitList.find(unit => unit.id === unitId);
  }

  getUnitLabel(unitId: string): string {
    let unit = this.getUnit(unitId);
    if (!unit) return 'Unknown unit';
    return unit.suffix ? `${unit.display} (${unit.suffix})` : unit.display;
  }

  private hasDuplicateName(name: string, excludedId?: string): boolean {
    let normalizedName = name.trim().toLocaleLowerCase();
    return this.dataCollectionDefinitions.some(definition =>
      definition.id !== excludedId && definition.name?.trim().toLocaleLowerCase() === normalizedName
    );
  }

  save() {
    if(!this.verify()) return;

    this.saving = true;
    this.dataLibraryService.update(this.dataCollectionDefinitions).then(() => {
      this.saving = false;
      this.initialDataCollectionDefinitions = this.helperService.deepCopy(this.dataCollectionDefinitions);
    });
  }

  verify() {
    let emptySubtasks = this.dataCollectionDefinitions.filter(i => i.name == '' || i.name == null || i.unit == '' || i.unit == null);

    if(emptySubtasks.length > 0) {
      this.notifierService.error('Please fill out all data collection definitions');
      return false;
    }

    let names = this.dataCollectionDefinitions.map(item => item.name.trim().toLocaleLowerCase());
    if (new Set(names).size !== names.length) {
      this.notifierService.error('Data Library item names must be unique');
      return false;
    }

    return true
  }
  
  migrate() {
    this.dialog.open(DataLibraryMigrateDialogComponent);
  }

}
